import { useState } from 'react';
import { PencilSimple, Question } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { EditPlanningIncomeDialog } from './EditPlanningIncomeDialog';
import { SlimMetricCard } from '../common/SlimMetricCard';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '../ui/Tooltip';
import { formatCyclePeriodLabel } from '../../utils/cyclePeriodLabel';
import { formatEUR } from '../../utils/money';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';

function PlanningIncomeLabel() {
  return (
    <span className="inline-flex items-center gap-space-2">
      {pt.budget.cycleIncome}
      <TooltipProvider>
        <TooltipRoot>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-ds-full border border-border text-muted-foreground/80"
              aria-label={pt.budget.cycleIncomeTooltip}
            >
              <Question className="h-2.5 w-2.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px]">
            {pt.budget.cycleIncomeTooltip}
          </TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    </span>
  );
}

/**
 * Rendimento usado no planeamento — por defeito ciclo anterior; editável via diálogo.
 */
export function BudgetCycleIncomeSection({
  overview,
  canEditBudgets,
}) {
  const [editOpen, setEditOpen] = useState(false);

  const cycleIncome = Number(overview?.cycle_income_eur ?? 0);
  const registeredIncome = Number(overview?.registered_cycle_income_eur ?? 0);
  const incomeBasis = overview?.income_basis ?? 'none';
  const isHistorical = Boolean(overview?.is_historical_cycle);

  if (!overview?.method) return null;

  const showPlanningSummary = Boolean(
    overview?.show_planning_summary ?? overview?.has_income,
  );
  if (isHistorical && showPlanningSummary) {
    return null;
  }

  if (isHistorical) {
    return (
      <SlimMetricCard
        label={<PlanningIncomeLabel />}
        value={registeredIncome}
        hint={
          registeredIncome > 0
            ? pt.budget.historicalCycleIncomeNote.replace(
                '{period}',
                formatCyclePeriodLabel(overview.period_start, overview.period_end),
              )
            : pt.budget.historicalCycleIncomeMissing
        }
      />
    );
  }

  if (!canEditBudgets && cycleIncome <= 0) return null;

  const incomeHint = (() => {
    if (incomeBasis === 'previous_cycle') {
      return pt.budget.planningIncome.defaultHint.replace(
        '{period}',
        formatCyclePeriodLabel(
          overview.previous_cycle_start,
          overview.previous_cycle_end,
        ),
      );
    }
    if (incomeBasis === 'manual') {
      return pt.budget.manualCycleIncomeNote;
    }
    if (incomeBasis === 'current_cycle' && registeredIncome > 0) {
      return pt.budget.currentCycleIncomeNote.replace(
        '{amount}',
        formatEUR(registeredIncome || 0),
      );
    }
    return null;
  })();

  const editButton = canEditBudgets ? (
    <button
      type="button"
      onClick={() => setEditOpen(true)}
      aria-label={pt.budget.planningIncome.editCta}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-ds-full bg-muted text-muted-foreground transition-colors hover:bg-stone-200 dark:hover:bg-stone-700"
    >
      <PencilSimple className="h-3.5 w-3.5" aria-hidden />
    </button>
  ) : null;

  return (
    <>
      <div className="flex flex-col gap-2">
        {cycleIncome > 0 ? (
          <SlimMetricCard
            label={<PlanningIncomeLabel />}
            value={cycleIncome}
            hint={incomeHint}
            trailing={editButton}
          />
        ) : (
          <p className="type-label font-normal rounded-ds-md border border-border bg-card px-space-3 py-space-2 text-muted-foreground">
            {pt.budget.planningIncomeEmpty}{' '}
            <Link
              to={`${paths.income}?register=1`}
              className="font-medium text-sage-on-tint underline-offset-2 hover:underline"
            >
              {pt.nav.income}
            </Link>
            {pt.budget.planningIncomeEmptySuffix}
          </p>
        )}
      </div>

      <EditPlanningIncomeDialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        overview={overview}
      />
    </>
  );
}
